import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import PageTitle from 'Components/PageTitle'
import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import ParameterFormFields from 'Services/ProductManagement/Features/Parameter/Components/ParameterFormFields'
import { SELECTED_PARAMETER_KEY } from 'Services/ProductManagement/Features/Parameter/Hooks/useParameterGroups'
import {
  useGetSingleParameterGroupQuery,
  useUpdateSingleParameterGroupMutation
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type {
  ParameterGroup,
  ParameterGroupParameter
} from 'Services/ProductManagement/Features/Parameter/types'
import type { ParameterTranslation } from 'Store'
import { PRODUCT_MANAGEMENT_ROOT_URL, TAB_PARAM_KEY } from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, DialogContentText, Grid, Typography } from '@mui/material'
import { type FormEvent, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function ParameterCreatePage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/product-parameters`
  const navigate = useNavigate()
  const { parameterGroupCategory } = useParams()
  const fallbackPage = `${baseUrl}?${TAB_PARAM_KEY}=${parameterGroupCategory}`

  const { data: singleParameterGroup = null, isLoading } = useGetSingleParameterGroupQuery({
    category: parameterGroupCategory as string
  })
  const [updateParameterGroup, { isLoading: isSubmitLoading }] =
    useUpdateSingleParameterGroupMutation()
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  let parameterGroupParameters = singleParameterGroup ? singleParameterGroup.parameters : []

  const breadcrumbs = [
    {
      title: t`Settings`
    },
    {
      title: t`Product parameters`,
      link: baseUrl
    },
    {
      title: singleParameterGroup
        ? getTranslation(singleParameterGroup.translations, 'fi').value
        : '',
      link: `${baseUrl}#${parameterGroupCategory}`
    },
    {
      title: t`Create new parameter`
    }
  ]

  const [parameterGroupParameterDetails, setParameterGroupParameterDetails] =
    useState<ParameterGroupParameter>({
      parameter_name: '',
      parameter_id: '',
      translations: [],
      minimum: null,
      maximum: null
    })

  /**
   * Update parameter group parameters with new parameter.
   * If parameter with the same ID already exists, show error message.
   */
  const updateParameterGroupParameters = () => {
    const foundParameterGroupParameterIndex = parameterGroupParameters.findIndex(
      parameterGroupParameter =>
        parameterGroupParameter.parameter_id === parameterGroupParameterDetails.parameter_id
    )

    if (foundParameterGroupParameterIndex !== -1) {
      SnackbarUtils.error(t`Parameter with this ID already exists.`)
      setIsSaveDialogOpen(false)
      return false
    }
    parameterGroupParameters = [...parameterGroupParameters, parameterGroupParameterDetails]
    return true
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!singleParameterGroup) {
      SnackbarUtils.error(t`There was an error creating the parameter.`)
      return
    }

    if (!updateParameterGroupParameters()) {
      return
    }

    const updatedParameterGroup: ParameterGroup = {
      ...singleParameterGroup,
      parameters: parameterGroupParameters
    }

    const params = {
      parameterGroupName: parameterGroupCategory as string,
      payload: updatedParameterGroup
    }
    updateParameterGroup(params)
      .unwrap()
      .then(() => {
        setIsSaveDialogOpen(false)
        navigate(
          appendQueryParamsToUrl(fallbackPage, {
            [SELECTED_PARAMETER_KEY]: parameterGroupParameterDetails.parameter_id
          })
        )
        SnackbarUtils.success(t`Parameter successfully created.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error creating the parameter.`)
      })
  }

  const setDetails = (
    value: string | number | ParameterTranslation[] | null,
    name: keyof ParameterGroupParameter
  ) => {
    setParameterGroupParameterDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleSubmit = async () => {
    formRef.current?.requestSubmit()
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={fallbackPage} />
          <PageTitle>{t`Create new parameter`}</PageTitle>
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <WithLoader loading={isLoading}>
            <Box>
              <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
                <ParameterFormFields
                  parameterGroupParameter={parameterGroupParameterDetails}
                  handleSetDetails={setDetails}
                  isEdit={false}
                />
                <SaveDialog
                  open={isSaveDialogOpen}
                  handleClose={() => setIsSaveDialogOpen(false)}
                  handleSubmit={handleSubmit}
                  loading={isSubmitLoading}
                  title={t`Create new parameter`}
                >
                  <DialogContentText>
                    <Typography component="span" color="text.primary">
                      {t`Are you sure you want to create a new parameter?`}
                    </Typography>
                  </DialogContentText>
                </SaveDialog>
                <CustomLoadingButton
                  isLoading={isSubmitLoading}
                  onClick={() => setIsSaveDialogOpen(true)}
                  size="large"
                  iconPosition="end"
                >
                  {t`Save parameter`}
                </CustomLoadingButton>
              </StyledForm>
            </Box>
          </WithLoader>
        </Grid>
      </Grid>
    </Box>
  )
}
